import React, { useEffect, useState } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import api, { formatGBP } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell } from "recharts";
import { Plus, Pencil, Trash2, Save, Loader2, Users, Package, TrendingUp } from "lucide-react";
import { RetailerFeed } from "@/components/admin/RetailerFeed";
import { CategoryManager } from "@/components/admin/CategoryManager";
import { BulkCategoryBar } from "@/components/admin/BulkCategoryBar";
import { ProductCategoryPicker } from "@/components/admin/ProductCategoryPicker";
import { BannerManager } from "@/components/admin/BannerManager";
import { AffiliateIntegrations } from "@/components/admin/AffiliateIntegrations";
import { AffiliateLinkEditor, affiliateLabel } from "@/components/admin/AffiliateLinkEditor";

const WEIGHT_KEYS = [
  { k: "interest", l: "Interest match" },
  { k: "budget", l: "Budget fit" },
  { k: "gift_type", l: "Gift-type / vibe match" },
  { k: "occasion", l: "Occasion & recipient" },
  { k: "popularity", l: "Popularity" },
  { k: "rating", l: "Rating" },
];

const empty = {
  name: "", brand: "", retailer: "", price: 25, image: "", url: "", category: "",
  interests: [], gift_types: [], occasions: [], recipients: [], age_min: 0, age_max: 99,
  rating: 4.5, reviews: 0, popularity: 0.5, in_stock: true,
};

export default function Admin() {
  const { user, loading } = useAuth();
  const [products, setProducts] = useState([]);
  const [weights, setWeights] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [editing, setEditing] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [savingW, setSavingW] = useState(false);
  const [selected, setSelected] = useState([]);
  const toggleSel = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const loadAll = () => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/weights").then((r) => setWeights(r.data)).catch(() => {});
    api.get("/admin/analytics").then((r) => setAnalytics(r.data)).catch(() => {});
  };

  useEffect(() => { if (user?.is_admin) loadAll(); }, [user]);

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-brand-green" /></div>;
  if (!user || !user.is_admin) {
    return <div className="max-w-md mx-auto py-24 text-center"><h1 className="font-serif text-3xl mb-3">Admin only</h1><p className="text-muted-foreground">Sign in with an admin account to access this area.</p></div>;
  }

  const openEdit = (p) => { setEditing(p ? { ...p } : { ...empty }); setDialogOpen(true); };

  const saveProduct = async () => {
    const body = {
      ...editing,
      price: parseFloat(editing.price), rating: parseFloat(editing.rating) || 4.5,
      reviews: parseInt(editing.reviews) || 0, popularity: parseFloat(editing.popularity),
      age_min: parseInt(editing.age_min) || 0, age_max: parseInt(editing.age_max) || 99,
      interests: toArr(editing.interests), gift_types: toArr(editing.gift_types),
      occasions: toArr(editing.occasions), recipients: toArr(editing.recipients),
    };
    try {
      if (editing.product_id) await api.put(`/admin/products/${editing.product_id}`, body);
      else await api.post("/admin/products", body);
      toast.success("Saved");
      setDialogOpen(false);
      loadAll();
    } catch { toast.error("Could not save product"); }
  };

  const del = async (id) => {
    await api.delete(`/admin/products/${id}`).catch(() => {});
    toast.success("Deleted");
    loadAll();
  };

  const saveWeights = async () => {
    setSavingW(true);
    try { await api.put("/admin/weights", weights); toast.success("Scoring weights updated"); }
    catch { toast.error("Could not save weights"); }
    finally { setSavingW(false); }
  };

  const funnelData = analytics ? [
    { name: "Quiz started", value: analytics.funnel.quiz_started },
    { name: "Completed", value: analytics.funnel.quiz_completed },
    { name: "Recs shown", value: analytics.funnel.recommendations_generated },
    { name: "Rec clicks", value: analytics.funnel.recommendation_click },
    { name: "Retailer clicks", value: analytics.funnel.retailer_click },
  ] : [];
  const colors = ["#1A3A2A", "#2f5641", "#C88A34", "#6B2D3A", "#8a3c4c"];

  return (
    <div className="max-w-7xl mx-auto px-4 lg:px-10 py-8 lg:py-12 gm-fade">
      <h1 className="font-serif text-3xl lg:text-5xl mb-2">Admin</h1>
      <p className="text-muted-foreground mb-8">Manage the catalogue, tune scoring, and watch the funnel.</p>

      <Tabs defaultValue="analytics">
        <TabsList className="bg-brand-cream2">
          <TabsTrigger value="analytics" data-testid="admin-tab-analytics">Analytics</TabsTrigger>
          <TabsTrigger value="catalogue" data-testid="admin-tab-catalogue">Catalogue</TabsTrigger>
          <TabsTrigger value="categories" data-testid="admin-tab-categories">Categories</TabsTrigger>
          <TabsTrigger value="scoring" data-testid="admin-tab-scoring">Scoring</TabsTrigger>
          <TabsTrigger value="feed" data-testid="admin-tab-feed">Retailer Feed</TabsTrigger>
          <TabsTrigger value="affiliate" data-testid="admin-tab-affiliate">Affiliate</TabsTrigger>
          <TabsTrigger value="banner" data-testid="admin-tab-banner">Homepage Banner</TabsTrigger>
        </TabsList>

        {/* Analytics */}
        <TabsContent value="analytics" className="mt-6">
          {!analytics ? <Loader2 className="w-5 h-5 animate-spin" /> : (
            <div className="space-y-6">
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Stat icon={TrendingUp} label="Completion rate" value={`${analytics.completion_rate}%`} />
                <Stat icon={TrendingUp} label="Rec click-through" value={`${analytics.recommendation_ctr}%`} />
                <Stat icon={Package} label="Catalogue size" value={analytics.total_products} />
                <Stat icon={Users} label="Registered users" value={analytics.total_users} />
              </div>
              <div className="bg-white rounded-2xl border border-brand-cream2 p-6">
                <h3 className="font-serif text-xl mb-4">Conversion funnel</h3>
                <div className="h-72" data-testid="admin-funnel-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={funnelData}>
                      <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke="#78716C" />
                      <YAxis allowDecimals={false} stroke="#78716C" />
                      <Tooltip />
                      <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                        {funnelData.map((e, i) => <Cell key={i} fill={colors[i]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div className="bg-white rounded-2xl border border-brand-cream2 p-6">
                <h3 className="font-serif text-xl mb-4">Most-clicked gifts</h3>
                {analytics.top_products.length === 0 ? (
                  <p className="text-muted-foreground text-sm">No retailer clicks yet.</p>
                ) : (
                  <ul className="space-y-2">
                    {analytics.top_products.map((t, i) => (
                      <li key={i} className="flex justify-between text-sm border-b border-brand-cream2 pb-2">
                        <span>{t.name} <span className="text-muted-foreground">· {t.brand}</span></span>
                        <span className="font-mono font-semibold">{t.clicks}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </TabsContent>

        {/* Catalogue */}
        <TabsContent value="catalogue" className="mt-6">
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-muted-foreground" data-testid="admin-catalogue-count">
              {products.length} products{selected.length ? ` · ${selected.length} selected` : ""}
            </p>
            <Button onClick={() => openEdit(null)} data-testid="admin-catalogue-add-gift-button"
              className="bg-brand-green hover:bg-brand-greendark rounded-full gap-2"><Plus className="w-4 h-4" /> Add gift</Button>
          </div>
          {selected.length > 0 && (
            <BulkCategoryBar selected={selected} onDone={() => { setSelected([]); loadAll(); }} />
          )}
          <div className="bg-white rounded-2xl border border-brand-cream2 overflow-hidden overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <input type="checkbox" data-testid="admin-select-all"
                      checked={selected.length > 0 && selected.length === products.length}
                      onChange={(e) => setSelected(e.target.checked ? products.map((p) => p.product_id) : [])} />
                  </TableHead>
                  <TableHead>Name</TableHead><TableHead>Retailer</TableHead>
                  <TableHead>Price</TableHead><TableHead>Category</TableHead><TableHead>Affiliate</TableHead><TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {products.map((p) => (
                  <TableRow key={p.product_id} data-testid={`admin-product-row-${p.product_id}`}>
                    <TableCell>
                      <input type="checkbox" data-testid={`admin-select-${p.product_id}`}
                        checked={selected.includes(p.product_id)} onChange={() => toggleSel(p.product_id)} />
                    </TableCell>
                    <TableCell className="font-medium max-w-[240px] truncate">{p.name}</TableCell>
                    <TableCell className="text-muted-foreground">{p.retailer}</TableCell>
                    <TableCell className="font-mono">{formatGBP(p.price)}</TableCell>
                    <TableCell className="capitalize">
                      {(p.categories || [p.category]).join(", ").replace(/-/g, " ")}
                      {p.manual_categories?.length ? (
                        <span className="ml-2 text-[11px] text-muted-foreground" data-testid={`admin-manual-${p.product_id}`}>hand-picked</span>
                      ) : null}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap"
                      data-testid={`admin-affiliate-${p.product_id}`}>{affiliateLabel(p)}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(p)} data-testid={`admin-edit-${p.product_id}`}><Pencil className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => del(p.product_id)} data-testid={`admin-delete-${p.product_id}`} className="text-brand-burgundy"><Trash2 className="w-4 h-4" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="categories" className="mt-6">
          <CategoryManager onChanged={loadAll} />
        </TabsContent>

        {/* Scoring */}
        <TabsContent value="scoring" className="mt-6">
          {!weights ? <Loader2 className="w-5 h-5 animate-spin" /> : (
            <div className="bg-white rounded-2xl border border-brand-cream2 p-6 max-w-2xl">
              <h3 className="font-serif text-xl mb-1">Recommendation scoring weights</h3>
              <p className="text-sm text-muted-foreground mb-6">Tune how much each factor influences ranking.</p>
              <div className="space-y-6">
                {WEIGHT_KEYS.map((w) => (
                  <div key={w.k}>
                    <div className="flex justify-between text-sm mb-2">
                      <Label>{w.l}</Label>
                      <span className="font-mono font-semibold">{Number(weights[w.k]).toFixed(2)}</span>
                    </div>
                    <Slider data-testid={`admin-scoring-weight-slider-${w.k}`}
                      value={[weights[w.k]]} min={0} max={1} step={0.05}
                      onValueChange={(v) => setWeights({ ...weights, [w.k]: v[0] })}
                      className="[&_[role=slider]]:bg-brand-green [&>span>span]:bg-brand-green" />
                  </div>
                ))}
              </div>
              <Button onClick={saveWeights} disabled={savingW} data-testid="admin-save-weights-button"
                className="mt-8 bg-brand-green hover:bg-brand-greendark rounded-full gap-2">
                {savingW ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save weights
              </Button>
            </div>
          )}
        </TabsContent>
        {/* Retailer Feed */}
        <TabsContent value="feed" className="mt-6">
          <RetailerFeed />
        </TabsContent>
        {/* Homepage banner */}
        <TabsContent value="banner" className="mt-6">
          <BannerManager />
        </TabsContent>
        {/* Affiliate */}
        <TabsContent value="affiliate" className="mt-6">
          <AffiliateIntegrations />
        </TabsContent>
      </Tabs>
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="admin-product-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{editing?.product_id ? "Edit gift" : "Add gift"}</DialogTitle>
            <DialogDescription>Catalogue facts shown to shoppers, plus how this product is filed.</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="grid grid-cols-2 gap-4 py-2">
              <Field label="Name" span><Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="admin-field-name" /></Field>
              <Field label="Brand"><Input value={editing.brand} onChange={(e) => setEditing({ ...editing, brand: e.target.value })} /></Field>
              <Field label="Retailer"><Input value={editing.retailer} onChange={(e) => setEditing({ ...editing, retailer: e.target.value })} /></Field>
              <Field label="Price (£)"><Input type="number" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} data-testid="admin-field-price" /></Field>
              <Field label="Headline category"><Input value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} /></Field>
              <Field label="Image URL" span><Input value={editing.image} onChange={(e) => setEditing({ ...editing, image: e.target.value })} /></Field>
              <Field label="Retailer / affiliate URL" span><Input value={editing.url} onChange={(e) => setEditing({ ...editing, url: e.target.value })} /></Field>
              <Field label="Interests (comma-sep)"><Input value={arrStr(editing.interests)} onChange={(e) => setEditing({ ...editing, interests: e.target.value })} /></Field>
              <Field label="Gift types (comma-sep)"><Input value={arrStr(editing.gift_types)} onChange={(e) => setEditing({ ...editing, gift_types: e.target.value })} /></Field>
              {editing.product_id && (
                <AffiliateLinkEditor product={editing}
                  onSaved={(p) => { setEditing(p); loadAll(); }} />
              )}
              {editing.product_id && (
                <ProductCategoryPicker product={editing}
                  onSaved={(p) => { setEditing(p); loadAll(); }} />
              )}
              <Field label="Occasions (comma-sep)"><Input value={arrStr(editing.occasions)} onChange={(e) => setEditing({ ...editing, occasions: e.target.value })} /></Field>
              <Field label="Recipients (comma-sep)"><Input value={arrStr(editing.recipients)} onChange={(e) => setEditing({ ...editing, recipients: e.target.value })} /></Field>
              <Field label="Rating"><Input type="number" step="0.1" value={editing.rating ?? ""} onChange={(e) => setEditing({ ...editing, rating: e.target.value })} /></Field>
              <Field label="Reviews"><Input type="number" value={editing.reviews ?? ""} onChange={(e) => setEditing({ ...editing, reviews: e.target.value })} /></Field>
            </div>
          )}
          <DialogFooter>
            <Button onClick={saveProduct} data-testid="admin-save-product-button" className="bg-brand-green hover:bg-brand-greendark rounded-full">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const toArr = (v) => Array.isArray(v) ? v : String(v || "").split(",").map((x) => x.trim()).filter(Boolean);
const arrStr = (v) => Array.isArray(v) ? v.join(", ") : v;

function Field({ label, span, children }) {
  return <div className={span ? "col-span-2" : ""}><Label className="text-xs mb-1 block">{label}</Label>{children}</div>;
}
function Stat({ icon: Icon, label, value }) {
  return (
    <div className="bg-white rounded-2xl border border-brand-cream2 p-5">
      <Icon className="w-5 h-5 text-brand-amber mb-3" />
      <p className="font-mono text-3xl font-bold text-brand-green">{value}</p>
      <p className="text-sm text-muted-foreground mt-1">{label}</p>
    </div>
  );
}
